import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import {
  AUDIENCE,
  AUDIENCE_LABELS,
  SCOPE,
  parseScope,
  type SkillAudience,
  type SkillFile,
  type SkillScope,
  type SkillVersionEntry,
  type SkillsResponse,
} from '@/pages/assistant/shared';

/**
 * The skill edit form — /assistant/skills/new creates, /:scope/:name/edit
 * updates (carrying the loaded version for the server's conflict check).
 * Arriving with a `restore` location state (from the view page's history)
 * pre-fills the form with that older version; saving writes a NEW version.
 */

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

/** The form's working copy. */
interface FormState {
  readonly name: string;
  readonly scope: SkillScope;
  readonly audience: SkillAudience;
  readonly description: string;
  readonly instructions: string;
  readonly tools: readonly string[];
}

export function SkillEditPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{ scope?: string; name?: string }>();
  const creating = params.name === undefined;
  const scope = parseScope(params.scope);
  const name = params.name ?? '';
  const restore = (useLocation().state as { restore?: SkillVersionEntry } | null)?.restore;

  const [form, setForm] = useState<FormState | null>(
    creating
      ? {
          name: '',
          scope: SCOPE.user,
          audience: AUDIENCE.internal,
          description: '',
          instructions: '',
          tools: [],
        }
      : null,
  );
  const [notice, setNotice] = useState<string | null>(null);

  const skill = useQuery({
    queryKey: ['assistant-skill', scope, name],
    enabled: !creating,
    queryFn: ({ signal }) => getJson<SkillFile>('/assistant/skills/file', { name, scope }, signal),
  });
  const index = useQuery({
    queryKey: ['assistant-skills'],
    queryFn: ({ signal }) => getJson<SkillsResponse>('/assistant/skills', undefined, signal),
  });
  const canManageGlobal = index.data?.canManageGlobal === true;

  // Seed the form once the skill loads — from the restored version when the
  // view page sent one, else from the current content.
  useEffect(() => {
    if (creating || skill.data === undefined || form !== null) return;
    const source = restore ?? skill.data;
    setForm({
      name: skill.data.name,
      scope: skill.data.scope,
      audience: skill.data.audience,
      description: source.description,
      instructions: source.instructions,
      tools: source.tools,
    });
  }, [creating, skill.data, form, restore]);

  const save = useMutation({
    mutationFn: (state: FormState) =>
      sendJson('put', '/assistant/skills/file', {
        name: state.name,
        scope: state.scope,
        audience: state.audience,
        description: state.description,
        instructions: state.instructions,
        tools: state.tools,
        ...(creating ? {} : { expectedVersion: skill.data?.version }),
      }),
    onSuccess: async (_data, state) => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-skills'] });
      await queryClient.invalidateQueries({ queryKey: ['assistant-skill', state.scope, state.name] });
      await queryClient.invalidateQueries({
        queryKey: ['assistant-skill-versions', state.scope, state.name],
      });
      navigate(`/assistant/skills/${state.scope}/${state.name}`);
    },
    onError: (error) => setNotice(errorMessage(error)),
  });

  if (!creating && skill.isPending) return <LoadingState label="Loading skill…" />;
  if (!creating && skill.isError) {
    return <ErrorNotice title="Skill not found" message={errorMessage(skill.error)} />;
  }
  if (form === null) return <LoadingState label="Loading skill…" />;

  const nameValid = NAME_PATTERN.test(form.name) && form.name.length >= 2;
  const canSave =
    nameValid && form.description.trim().length > 0 && form.instructions.trim().length > 0;
  const cancelTo = creating ? '/assistant/skills' : `/assistant/skills/${scope}/${name}`;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="AI ASSISTANT"
        title={creating ? 'New skill' : `Editing ${form.name}`}
        subtitle={
          creating
            ? 'A reusable workflow the assistant follows step by step'
            : restore
              ? `Restoring v${restore.version} — saving writes a new version`
              : 'The assistant uses the saved version immediately'
        }
      />
      <Panel className="p-5">
        {notice === null ? null : (
          <div className="mb-3">
            <ErrorNotice inline title="Could not save" message={notice} />
          </div>
        )}
        <div className="space-y-4">
          {creating ? (
            <label className="block">
              <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
                Name (kebab-case, permanent)
              </span>
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value.toLowerCase() })}
                placeholder="morning-shipment-check"
                maxLength={60}
                className="w-full rounded-md border border-admin-line px-2 py-1.5 font-mono text-portal-small focus:border-brand/60 focus:outline-none"
              />
            </label>
          ) : null}
          {creating && canManageGlobal ? (
            <label className="block">
              <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
                Scope
              </span>
              <select
                value={form.scope}
                onChange={(event) => setForm({ ...form, scope: event.target.value as SkillScope })}
                className="rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none"
              >
                <option value={SCOPE.user}>Personal — only you</option>
                <option value={SCOPE.global}>Organization — everyone</option>
              </select>
            </label>
          ) : null}
          {form.scope === SCOPE.global && canManageGlobal ? (
            <label className="block">
              <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
                Audience
              </span>
              <select
                value={form.audience}
                onChange={(event) =>
                  setForm({ ...form, audience: event.target.value as SkillAudience })
                }
                className="rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none"
              >
                <option value={AUDIENCE.internal}>{AUDIENCE_LABELS[AUDIENCE.internal]}</option>
                <option value={AUDIENCE.internalAndContractors}>
                  {AUDIENCE_LABELS[AUDIENCE.internalAndContractors]}
                </option>
              </select>
            </label>
          ) : null}
          <label className="block">
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              When to use it (shown to the assistant)
            </span>
            <input
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              maxLength={200}
              className="w-full rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              Instructions (markdown, numbered steps)
            </span>
            <textarea
              value={form.instructions}
              onChange={(event) => setForm({ ...form, instructions: event.target.value })}
              maxLength={8000}
              className="field-sizing-content min-h-64 w-full resize-y rounded-md border border-admin-line p-2 font-mono text-portal-micro leading-relaxed focus:border-brand/60 focus:outline-none"
            />
          </label>
          {form.tools.length > 0 ? (
            <div>
              <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
                Tools this skill uses (edit via the assistant)
              </span>
              <span className="flex flex-wrap gap-1.5">
                {form.tools.map((tool) => (
                  <Pill key={tool}>{tool}</Pill>
                ))}
              </span>
            </div>
          ) : null}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate(cancelTo)}>
            Cancel
          </Button>
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate(form)}>
            {save.isPending ? 'Saving…' : 'Save skill'}
          </Button>
        </div>
      </Panel>
    </section>
  );
}
